import { createContext, useMemo, useContext, useState } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const SiteContext = /*#__PURE__*/createContext(null);
const defaultProps = {
  dateFormat: {
    date: 'yyyy-MM-dd',
    datetime: 'yyyy-MM-dd HH:mm',
    dayDdmmmTime: 'ddMMM HH:mm',
    dayDdmmm: 'ddMMM',
    time: 'HH:mm',
    day: 'd MMM',
    dayYear: 'd MMM yyyy',
    dayTime: 'd MMM HH:mm',
    weekday: 'EEEE d MMM',
    shortWeekday: 'E d MMM',
    shortWeekdayYear: 'E d MMM yyyy',
    shortWeekdayFullYear: 'E d MMMM, yyyy',
    weekdayYear: 'EEEE d MMMM, yyyy',
    monthYear: 'MMM yyyy',
    longMonthYear: 'MMMM yyyy',
    timeWeekdayYear: 'HH:mm EEEE d MMMM, yyyy',
    shortDayShortMonth: 'E d MMM',
    dayName: 'EEEE'
  },
  timeFormat: {
    hour: 'h',
    minute: 'min',
    second: 's'
  },
  language: {
    code: 'en',
    name: 'English',
    locale: 'en_GB'
  },
  brand: {
    code: 'GOTOGATE',
    name: 'GoToGate/FB'
  }
};
const SiteContextProvider = _ref => {
  let {
    agencyAccount,
    availableLanguages,
    brand = defaultProps.brand,
    dateFormat = defaultProps.dateFormat,
    entryType,
    headerAndInfoHeight,
    language = defaultProps.language,
    market,
    partner,
    siteCurrency,
    siteName,
    timeFormat = defaultProps.timeFormat,
    ...rest
  } = _ref;
  const [headerHeightContext, setHeaderHeightContext] = useState(headerAndInfoHeight);
  const handleHeaderHeightContext = newHeight => setHeaderHeightContext(newHeight);
  const [mobileCartHeightContext, setMobileCartHeightContext] = useState(0);
  const handleMobileCartHeightContext = newHeight => setMobileCartHeightContext(newHeight);
  return /*#__PURE__*/_jsx(SiteContext.Provider, {
    value: {
      agencyAccount,
      availableLanguages,
      brand,
      dateFormat,
      entryType,
      mobileCartHeightContext,
      headerHeightContext,
      language,
      market,
      partner,
      handleMobileCartHeightContext,
      handleHeaderHeightContext,
      siteCurrency,
      siteName,
      timeFormat
    },
    ...rest
  });
};
const generateErrorMessage = text => `${text} must be used within a SiteContextProvider`;

// TODO remove this once the data is normalized on IBE: https://jira.etraveli.net/browse/WEB-1981
const normalizeBrandCode = brand => {
  var _brand$code;
  const code = ((_brand$code = brand === null || brand === void 0 ? void 0 : brand.code) !== null && _brand$code !== void 0 ? _brand$code : '').toLowerCase();
  return {
    ...brand,
    code
  };
};
const useDateFormat = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error(generateErrorMessage('useDateFormat'));
  }
  return useMemo(() => context.dateFormat, [context.dateFormat]);
};
const useHeightContext = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error(generateErrorMessage('useHeightContext'));
  }
  const {
    headerHeightContext,
    handleMobileCartHeightContext,
    handleHeaderHeightContext,
    mobileCartHeightContext
  } = context;
  return useMemo(() => {
    return {
      headerHeightContext,
      handleMobileCartHeightContext,
      handleHeaderHeightContext,
      mobileCartHeightContext
    };
  }, [headerHeightContext, handleMobileCartHeightContext, handleHeaderHeightContext, mobileCartHeightContext]);
};
const useSiteContext = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error(generateErrorMessage('useSiteContext'));
  }
  const {
    agencyAccount,
    availableLanguages,
    brand,
    entryType,
    language,
    market,
    partner,
    siteCurrency,
    siteName,
    timeFormat
  } = context;
  return useMemo(() => {
    return {
      agencyAccount,
      availableLanguages,
      brand: normalizeBrandCode(brand),
      entryType,
      language,
      market,
      partner,
      siteCurrency,
      siteName,
      timeFormat
    };
  }, [agencyAccount, availableLanguages, brand, entryType, language, market, partner, siteCurrency, siteName, timeFormat]);
};
export { SiteContextProvider, useDateFormat, useHeightContext, useSiteContext };
//# sourceMappingURL=SiteContext.js.map